import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { EmptyState } from '@kardume/ui';
import { BackofficeSectionId, getInitialSection, isSectionVisible } from './utils/access';
import DashboardPage from './pages/DashboardPage';
import MerchantsPage from './pages/MerchantsPage';
import FeaturesPage from './pages/FeaturesPage';
import CustomersPage from './pages/CustomersPage';
import ModerationPage from './pages/ModerationPage';
import ExperienceModerationPage from './pages/ExperienceModerationPage';
import ClaimsPage from './pages/ClaimsPage';
import ProfileEditsPage from './pages/ProfileEditsPage';
import PromotionRulesPage from './pages/PromotionRulesPage';
import BenefitTypesPage from './pages/BenefitTypesPage';
import LeadsPage from './pages/LeadsPage';
import MessagingPage from './pages/MessagingPage';
import CategoriesPage from './pages/CategoriesPage';
import AmenitiesPage from './pages/AmenitiesPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import SearchTermsPage from './pages/SearchTermsPage';
import LocalitiesPage from './pages/LocalitiesPage';
import FeedsPage from './pages/FeedsPage';
import TrailsPage from './pages/TrailsPage';
import CollectionsPage from './pages/CollectionsPage';
import PassPlansPage from './pages/PassPlansPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import BillingPage from './pages/BillingPage';
import InfluencersPage from './pages/InfluencersPage';
import ReportsPage from './pages/ReportsPage';
import MerchantInterestPage from './pages/MerchantInterestPage';
import OpportunitiesPage from './pages/OpportunitiesPage';
import AuditLogsPage from './pages/AuditLogsPage';
import EmployeesPage from './pages/EmployeesPage';
import AccessProfilesPage from './pages/AccessProfilesPage';

interface BackofficeRoutesProps {
  activeSection: BackofficeSectionId;
  permissions: string[];
  isAdmin: boolean;
}

const AccessDenied = () => (
  <EmptyState
    icon={<ShieldAlert size={30} />}
    title="Acesso restrito"
    description="Seu perfil não tem permissão para acessar esta área."
  />
);

export function BackofficeRoutes({ activeSection, permissions, isAdmin }: BackofficeRoutesProps) {
  const section = getInitialSection(permissions, activeSection, isAdmin);
  if (!section || !isSectionVisible(permissions, section, isAdmin)) {
    return <AccessDenied />;
  }

  switch (section) {
    case 'dashboard':
      return <DashboardPage />;
    case 'merchants':
      return <MerchantsPage />;
    case 'features':
      return <FeaturesPage permissions={permissions} isAdmin={isAdmin} />;
    case 'customers':
      return <CustomersPage />;
    case 'claims':
      return <ClaimsPage permissions={permissions} isAdmin={isAdmin} />;
    case 'profile-edits':
      return <ProfileEditsPage permissions={permissions} isAdmin={isAdmin} />;
    case 'moderation':
      return <ModerationPage />;
    case 'experience-moderation':
      return <ExperienceModerationPage />;
    case 'promotion-rules':
      return <PromotionRulesPage />;
    case 'benefit-types':
      return <BenefitTypesPage />;
    case 'leads':
      return <LeadsPage />;
    case 'messaging':
      return <MessagingPage permissions={permissions} isAdmin={isAdmin} />;
    case 'categories':
      return <CategoriesPage />;
    case 'amenities':
      return <AmenitiesPage />;
    case 'payment-methods':
      return <PaymentMethodsPage />;
    case 'search-terms':
      return <SearchTermsPage />;
    case 'localities':
      return <LocalitiesPage />;
    case 'feeds':
      return <FeedsPage />;
    case 'trails':
      return <TrailsPage />;
    case 'collections':
      return <CollectionsPage />;
    case 'plans':
      return <PassPlansPage />;
    case 'subscriptions':
      return <SubscriptionsPage />;
    case 'billing':
      return <BillingPage />;
    case 'influencers':
      return <InfluencersPage permissions={permissions} isAdmin={isAdmin} />;
    case 'reports':
      return <ReportsPage />;
    case 'merchant-interest':
      return <MerchantInterestPage />;
    case 'opportunities':
      return <OpportunitiesPage />;
    case 'audit':
      return <AuditLogsPage />;
    case 'employees':
      return <EmployeesPage />;
    case 'access-profiles':
      return <AccessProfilesPage />;
    default:
      return <AccessDenied />;
  }
}
